import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  invitationService,
  type InviterInfo,
  type VerifyInvitationResponse,
} from "@/services/invitationService";

// 重新导出类型供外部使用
export type { InviterInfo, VerifyInvitationResponse as InviteVerifyResponse };

/**
 * useInviteToken Hook 返回值接口
 */
export interface UseInviteTokenResult {
  /** 邀请 token（从 URL 解析） */
  inviteToken: string | null;
  /** 邀请是否有效 */
  isValid: boolean;
  /** 邀请人信息 */
  inviter: InviterInfo | null;
  /** 奖励积分数 */
  rewardPoints: number;
  /** 错误信息 */
  error: string | null;
  /** 是否正在加载 */
  loading: boolean;
  /** 重新验证 token */
  refetch: () => Promise<void>;
}

/**
 * 从 URL 查询参数中解析 invite_token
 * @param searchParams URLSearchParams 对象
 * @returns invite_token 值或 null
 */
export function parseInviteToken(searchParams: URLSearchParams): string | null {
  const token = searchParams.get("invite_token");
  return token && token.trim() !== "" ? token.trim() : null;
}

/**
 * 邀请 Token Hook
 *
 * 功能：
 * 1. 从 URL 参数解析 invite_token
 * 2. 调用验证 API 获取邀请人信息
 * 3. 管理 token 状态和加载状态
 *
 * @returns {UseInviteTokenResult} 邀请 token 相关状态和方法
 *
 * @example
 * const { inviteToken, isValid, inviter, rewardPoints, loading, error } = useInviteToken();
 *
 * if (loading) return <Spinner />;
 * if (isValid && inviter) {
 *   return <InvitationBanner inviter={inviter} rewardPoints={rewardPoints} />;
 * }
 */
export function useInviteToken(): UseInviteTokenResult {
  const [searchParams] = useSearchParams();
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);
  const [inviter, setInviter] = useState<InviterInfo | null>(null);
  const [rewardPoints, setRewardPoints] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * 验证邀请 token
   */
  const verifyToken = useCallback(async (token: string) => {
    try {
      const data = await invitationService.verifyInvitation(token);

      if (data.valid) {
        setIsValid(true);
        setInviter(data.inviter || null);
        setRewardPoints(data.reward_points || 0);
        setError(null);
      } else {
        setIsValid(false);
        setInviter(null);
        setRewardPoints(0);
        setError(data.error || "邀请码无效");
      }
    } catch (err) {
      console.error("Failed to verify invite token:", err);
      setIsValid(false);
      setInviter(null);
      setRewardPoints(0);
      setError(err instanceof Error ? err.message : "验证失败");
    }
  }, []);

  /**
   * 重新获取/验证 token
   */
  const refetch = useCallback(async () => {
    const token = parseInviteToken(searchParams);
    setInviteToken(token);

    if (!token) {
      setIsValid(false);
      setInviter(null);
      setRewardPoints(0);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    await verifyToken(token);
    setLoading(false);
  }, [searchParams, verifyToken]);

  // 初始化时解析 URL 并验证 token
  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    inviteToken,
    isValid,
    inviter,
    rewardPoints,
    error,
    loading,
    refetch,
  };
}

export default useInviteToken;
